import { Direction } from '@angular/cdk/bidi';
import { ConnectedPosition } from '@angular/cdk/overlay';

import { PLACEMENT } from '@ideals/types';

export type TooltipSide = 'bottom' | 'end' | 'start' | 'top';

type AutoPlacement = PLACEMENT.Auto | PLACEMENT.AutoEnd | PLACEMENT.AutoStart;
type ExplicitPlacement = Exclude<PLACEMENT, AutoPlacement>;
type ConnectionPoints = Pick<ConnectedPosition, 'originX' | 'originY' | 'overlayX' | 'overlayY'>;
type ConnectionOffset = Pick<ConnectedPosition, 'offsetX' | 'offsetY'>;

export const TOOLTIP_OFFSET = 8;

const AUTO_ORDER: readonly ExplicitPlacement[] = [PLACEMENT.Bottom, PLACEMENT.Right, PLACEMENT.Left, PLACEMENT.Top];

const CONNECTION_POINTS: Readonly<Record<ExplicitPlacement, ConnectionPoints>> = {
  [PLACEMENT.Bottom]: { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
  [PLACEMENT.BottomEnd]: { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  [PLACEMENT.BottomStart]: { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  [PLACEMENT.Left]: { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' },
  [PLACEMENT.LeftEnd]: { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' },
  [PLACEMENT.LeftStart]: { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
  [PLACEMENT.Right]: { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
  [PLACEMENT.RightEnd]: { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
  [PLACEMENT.RightStart]: { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
  [PLACEMENT.Top]: { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
  [PLACEMENT.TopEnd]: { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
  [PLACEMENT.TopStart]: { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
};

function isAutoPlacement(placement: PLACEMENT): placement is AutoPlacement {
  return placement === PLACEMENT.Auto || placement === PLACEMENT.AutoEnd || placement === PLACEMENT.AutoStart;
}

function getOffset(side: TooltipSide, direction: Direction): ConnectionOffset {
  const inlineOffset = direction === 'rtl' ? -TOOLTIP_OFFSET : TOOLTIP_OFFSET;
  const offsets: Readonly<Record<TooltipSide, ConnectionOffset>> = {
    bottom: { offsetY: TOOLTIP_OFFSET },
    end: { offsetX: inlineOffset },
    start: { offsetX: -inlineOffset },
    top: { offsetY: -TOOLTIP_OFFSET },
  };

  return offsets[side];
}

function toConnectedPosition(placement: ExplicitPlacement, direction: Direction): ConnectedPosition {
  const points = CONNECTION_POINTS[placement] ?? CONNECTION_POINTS[PLACEMENT.Bottom];

  return { ...points, ...getOffset(getTooltipSide(points), direction) };
}

export function getTooltipSide({ originX, originY, overlayY }: ConnectionPoints): TooltipSide {
  if (originY === 'bottom' && overlayY === 'top') {
    return 'bottom';
  }

  if (originY === 'top' && overlayY === 'bottom') {
    return 'top';
  }

  return originX === 'end' ? 'end' : 'start';
}

export function getTooltipPositions(placement: PLACEMENT, behaviour: readonly PLACEMENT[], direction: Direction): ConnectedPosition[] {
  const placements = [placement, ...behaviour].flatMap((item) => isAutoPlacement(item) ? AUTO_ORDER : [item]);

  return [...new Set(placements)].map((item) => toConnectedPosition(item, direction));
}
